import React from 'react';
import classnames from 'classnames';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import { useTranslation } from 'src/i18n';
import { EmploymentRecord } from 'utils/employment/types';
import { buildEmploymentBadge, buildEmploymentSubtitle, buildEmploymentTitle, formatEmploymentPeriod } from 'utils/employment/formatters';

import styles from './BiographyEmploymentTimeline.module.scss';

export interface ReadOnlyEmploymentCardProps {
  /** Employment record to display */
  employment: EmploymentRecord;
  /** Index in the timeline */
  index: number;
  /** Whether this is the last card (affects connecting line) */
  isLast: boolean;
  /** Test ID for testing */
  testId?: string;
}

const ReadOnlyEmploymentCard: React.FC<ReadOnlyEmploymentCardProps> = ({
  employment,
  index,
  isLast,
  testId,
}) => {
  const { t } = useTranslation('NS_EDIT');

  const badge = buildEmploymentBadge(employment, t);
  const subtitle = buildEmploymentSubtitle(employment, t);
  
  // Check if employment has date information for dynamic height
  const hasDateInfo = formatEmploymentPeriod(employment.startDate, employment.endDate, t) !== '';

  // Helper function to get badge styles (same as OBArrayItemCard)
  const getBadgeStyles = (variant: 'primary' | 'info' | 'performance') => {
    switch (variant) {
      case 'performance':
        return {
          backgroundColor: '#f0f2f5',
          color: '#65676b',
          fontSize: '10px',
          height: '16px',
          border: 'none',
        };
      case 'primary':
        return {
          backgroundColor: '#1877f2',
          color: '#fff',
          fontSize: '10px',
          height: '16px',
          border: 'none',
        };
      case 'info':
      default:
        return {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          fontSize: '10px',
          height: '16px',
          border: 'none',
        };
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        marginBottom: { xs: 0.3 }, // Much smaller margin like edit section
      }}
      data-testid={testId}
    >
      {/* Timeline Connector - with proper line connections */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: { xs: '24px', sm: '28px' },
          minHeight: { xs: '80px', sm: '90px' }, // Fixed height like edit section
        }}
      >
        {/* Top connecting line - for items that are not first */}
        {index !== 0 && (
          <Box
            sx={{
              width: '2px',
              height: { xs: '40px', sm: '45px' }, // Fixed height like edit section
              borderLeft: '2px dotted #d1d5db',
              opacity: 0.8,
              position: 'absolute',
              top: 0, // Simple positioning like edit section
            }}
          />
        )}

        {/* Timeline Dot - Neutral colors */}
        <Box
          sx={{
            width: { xs: '12px', sm: '14px' },
            height: { xs: '12px', sm: '14px' },
            borderRadius: '50%',
            backgroundColor: '#9ca3af',
            border: '3px solid #fff',
            boxShadow: '0 0 0 2px #9ca3af',
            flexShrink: 0,
            zIndex: 2,
            transition: 'all 0.2s ease-in-out',
          }}
        />

        {/* Bottom connecting line - for items that are not last */}
        {!isLast && (
          <Box
            sx={{
              width: '2px',
              height: { xs: '40px', sm: '45px' }, // Fixed height like edit section
              borderLeft: '2px dotted #d1d5db',
              opacity: 0.8,
              position: 'absolute',
              bottom: 0, // Simple positioning like edit section
            }}
          />
        )}
      </Box>

      {/* Employment Content - styled exactly like OBArrayItemCard */}
      <Box
        sx={{
          flex: 1,
          minWidth: { xs: 280, sm: 320, md: 360 },
          maxWidth: { xs: '100%', sm: '600px', md: '700px' }, // Constrain card width
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #e4e6ea',
            borderRadius: '8px',
            minHeight: badge ? (hasDateInfo ? '84px' : '70px') : (hasDateInfo ? '64px' : '50px'),
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: '12px 16px',
            '&:hover': {
              borderColor: '#dadde1',
            },
          }}
        >
          {/* Badge - positioned absolutely like OBArrayItemCard */}
          {badge && (
            <Box sx={{ position: 'absolute', top: 4, left: 8 }}>
              <Chip label={badge.label} size="small" sx={getBadgeStyles(badge.variant)} />
            </Box>
          )}

          {/* Main Content */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              paddingTop: badge ? '12px' : 0, // Add top padding when badge is present
            }}
          >
            {/* Title */}
            <Typography
              variant="body1"
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#1c1e21',
                lineHeight: '20px',
                marginBottom: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {buildEmploymentTitle(employment, t)}
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: '#65676b',
                lineHeight: '16px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReadOnlyEmploymentCard; 